import React, { useState, useRef, useMemo, useCallback } from 'react';
import Icon from './Icon';
import useClickOutside from '../../hooks/useClickOutside';
import { getFeatureLabel, getFeatureIcon } from '../analysis/SerpFeatureIcons';
import './SerpFeaturesFilter.css';

const buildFeatureCounts = (keywords) => {
  const counts = new Map();
  for (const kw of keywords) {
    const features = kw?.serpFeatures;
    if (!Array.isArray(features)) continue;
    for (const feature of features) {
      if (!feature) continue;
      counts.set(feature, (counts.get(feature) || 0) + 1);
    }
  }
  return counts;
};

const buildAvailableFeatures = (keywords) => {
  const counts = buildFeatureCounts(keywords);
  const entries = Array.from(counts.entries());
  entries.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return entries.map(([feature, count]) => ({ feature, count }));
};

const SerpFeaturesFilter = ({ keywords = [], selected = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleCloseDropdown = useCallback(() => setIsOpen(false), []);
  useClickOutside(dropdownRef, handleCloseDropdown);

  const availableFeatures = useMemo(() => buildAvailableFeatures(keywords), [keywords]);

  if (availableFeatures.length === 0) {
    return null;
  }

  const handleToggle = () => setIsOpen(!isOpen);

  const handleFeatureToggle = (feature) => {
    const isSelected = selected.includes(feature);
    const next = isSelected
      ? selected.filter((f) => f !== feature)
      : [...selected, feature];
    onChange(next);
  };

  const handleSelectAll = () => {
    const allFeatures = availableFeatures.map((entry) => entry.feature);
    const isAllSelected = selected.length === allFeatures.length;
    onChange(isAllSelected ? [] : allFeatures);
  };

  const handleClearAll = () => onChange([]);

  const isAllSelected = selected.length === availableFeatures.length;
  const displayText = selected.length === 0
    ? 'SERP features'
    : selected.length === 1
      ? getFeatureLabel(selected[0])
      : `${selected.length} sélectionnées`;

  const hasSelection = selected.length > 0;
  const triggerAriaLabel = hasSelection
    ? `Filtre SERP features : ${displayText}`
    : 'Ouvrir le filtre SERP features';

  return (
    <div className="serp-features-filter" ref={dropdownRef}>
      <button
        className={`serp-features-filter-trigger ${hasSelection ? 'has-selection' : ''}`}
        onClick={handleToggle}
        type="button"
        aria-label={triggerAriaLabel}
      >
        <Icon name="Filter" size={16} />
        <span className="filter-text">{displayText}</span>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={14} />
      </button>

      {isOpen && (
        <div className="serp-features-filter-dropdown">
          <div className="serp-features-filter-header">
            <button
              className="filter-action-btn"
              onClick={handleSelectAll}
              type="button"
            >
              <Icon name={isAllSelected ? 'CheckSquare' : 'Square'} size={16} />
              {isAllSelected ? 'Désélectionner tout' : 'Sélectionner tout'}
            </button>
            {hasSelection && !isAllSelected && (
              <button
                className="filter-action-btn secondary"
                onClick={handleClearAll}
                type="button"
              >
                <Icon name="X" size={16} />
                Effacer
              </button>
            )}
          </div>

          <div className="serp-features-filter-list">
            {availableFeatures.map(({ feature, count }) => {
              const isSelected = selected.includes(feature);
              return (
                <label
                  key={feature}
                  className={`serp-features-filter-item ${isSelected ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleFeatureToggle(feature)}
                  />
                  <Icon name={isSelected ? 'CheckSquare' : 'Square'} size={16} />
                  <span className="serp-features-filter-icon">
                    <Icon name={getFeatureIcon(feature)} size={14} />
                  </span>
                  <span className="serp-features-filter-name">{getFeatureLabel(feature)}</span>
                  <span className="serp-features-filter-count">{count}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SerpFeaturesFilter;
